import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { authUpdateMe, authChangePassword } from '../api';

interface EditForm {
  full_name: string;
  email: string;
  company: string;
  role: string;
  phone: string;
}

interface PasswordForm {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

const LOCAL_EXTRA_KEY = 'asin-tracker-profile-extra';

const getInitials = (name: string): string => {
  if (!name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
};

const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>({ full_name: '', email: '', company: '', role: '', phone: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState<PasswordForm>({ old_password: '', new_password: '', confirm_password: '' });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Extra profile fields stored locally (company, role, phone)
  const [extra, setExtra] = useState<{ company: string; role: string; phone: string }>({ company: '', role: '', phone: '' });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(LOCAL_EXTRA_KEY);
      if (stored) setExtra(JSON.parse(stored));
    } catch { /* ignore */ }
  }, []);

  const handleEdit = () => {
    setEditForm({
      full_name: user?.full_name || '',
      email: user?.email || '',
      company: extra.company,
      role: extra.role,
      phone: extra.phone,
    });
    setEditing(true);
    setMessage('');
    setError('');
  };

  const handleSave = async () => {
    if (!editForm.full_name.trim()) return;
    setSaving(true);
    setError('');

    try {
      await authUpdateMe({
        full_name: editForm.full_name.trim(),
        email: editForm.email.trim(),
      });

      const newExtra = { company: editForm.company, role: editForm.role, phone: editForm.phone };
      setExtra(newExtra);
      localStorage.setItem(LOCAL_EXTRA_KEY, JSON.stringify(newExtra));

      await refreshUser();
      setEditing(false);
      setMessage('Profile updated successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setError('');
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    if (passwordForm.new_password.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('Passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await authChangePassword({
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password,
      });
      setPasswordMessage('Password changed successfully');
      setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
      setShowPasswordForm(false);
      setTimeout(() => setPasswordMessage(''), 3000);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setPasswordError(typeof detail === 'string' ? detail : 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const initials = user ? getInitials(user.full_name) : '?';
  const memberSince = user?.created_at
    ? new Date(user.created_at as unknown as string).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  return (
    <div>
      <div className="page-header">
        <h2>Profile</h2>
        <p>Manage your personal information and account security</p>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {passwordMessage && <div className="alert alert-success">{passwordMessage}</div>}

      {/* Profile Card */}
      <div className="card" style={{ maxWidth: '720px' }}>
        <div className="profile-hero">
          <div className="profile-avatar-lg">{initials}</div>
          <div className="profile-hero-info">
            <h3 className="profile-hero-name">{user?.full_name || 'Your Name'}</h3>
            <p className="profile-hero-role">
              {[extra.role, extra.company].filter(Boolean).join(' at ') || 'Set up your profile'}
            </p>
            {user?.email && (
              <p className="profile-hero-email">{user.email}</p>
            )}
            {memberSince && (
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                Member since {memberSince}
              </p>
            )}
          </div>
          {!editing && (
            <button className="btn btn-secondary btn-small" onClick={handleEdit} style={{ marginLeft: 'auto', alignSelf: 'flex-start' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Details Card */}
      <div className="card" style={{ maxWidth: '720px' }}>
        <div className="card-header">
          <span className="card-title">Personal Information</span>
        </div>

        {error && <div className="alert alert-error" style={{ margin: '0 0 16px' }}>{error}</div>}

        {editing ? (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                  placeholder="e.g. John Smith"
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="e.g. john@company.com"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Company</label>
                <input
                  value={editForm.company}
                  onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                  placeholder="e.g. Acme Corp"
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <input
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  placeholder="e.g. Product Manager"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="e.g. +1 555-0123"
                />
              </div>
              <div className="form-group" />
            </div>
            <div className="modal-footer" style={{ borderTop: 'none', paddingTop: '8px' }}>
              <button className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={!editForm.full_name.trim() || saving}>
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </>
        ) : (
          <div className="profile-details-grid">
            <div className="profile-field">
              <span className="profile-field-label">Full Name</span>
              <span className="profile-field-value">{user?.full_name || '\u2014'}</span>
            </div>
            <div className="profile-field">
              <span className="profile-field-label">Email</span>
              <span className="profile-field-value">{user?.email || '\u2014'}</span>
            </div>
            <div className="profile-field">
              <span className="profile-field-label">Company</span>
              <span className="profile-field-value">{extra.company || '\u2014'}</span>
            </div>
            <div className="profile-field">
              <span className="profile-field-label">Role</span>
              <span className="profile-field-value">{extra.role || '\u2014'}</span>
            </div>
            <div className="profile-field">
              <span className="profile-field-label">Phone</span>
              <span className="profile-field-value">{extra.phone || '\u2014'}</span>
            </div>
            <div className="profile-field">
              <span className="profile-field-label">Account Status</span>
              <span className="profile-field-value">
                <span className="status-badge status-matched" style={{ fontSize: '11px' }}>Active</span>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Security Card */}
      <div className="card" style={{ maxWidth: '720px' }}>
        <div className="card-header">
          <span className="card-title">Security</span>
          {!showPasswordForm && (
            <button className="btn btn-secondary btn-small" onClick={() => { setShowPasswordForm(true); setPasswordError(''); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Change Password
            </button>
          )}
        </div>

        {showPasswordForm ? (
          <>
            {passwordError && <div className="alert alert-error" style={{ margin: '0 0 16px' }}>{passwordError}</div>}
            <div className="form-row">
              <div className="form-group">
                <label>Current Password *</label>
                <input
                  type="password"
                  value={passwordForm.old_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                />
              </div>
              <div className="form-group" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>New Password *</label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  placeholder="Min. 6 characters"
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password *</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div className="modal-footer" style={{ borderTop: 'none', paddingTop: '8px' }}>
              <button className="btn btn-secondary" onClick={() => { setShowPasswordForm(false); setPasswordError(''); setPasswordForm({ old_password: '', new_password: '', confirm_password: '' }); }}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleChangePassword}
                disabled={!passwordForm.old_password || !passwordForm.new_password || !passwordForm.confirm_password || changingPassword}
              >
                {changingPassword ? 'Changing...' : 'Update Password'}
              </button>
            </div>
          </>
        ) : (
          <div className="profile-details-grid">
            <div className="profile-field">
              <span className="profile-field-label">Password</span>
              <span className="profile-field-value">••••••••</span>
            </div>
            <div className="profile-field">
              <span className="profile-field-label">Account Type</span>
              <span className="profile-field-value">{user?.is_admin ? 'Administrator' : 'Standard User'}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
